import {
  userIsAbrechnung,
  userIsBuero,
  userIsManagement,
  userIsMonteure,
  type CurrentUser,
} from "@/lib/auth";
import type { LieferscheinSummary } from "@/lib/lieferscheine";
import type { TimesheetSummary } from "@/lib/timesheets";
import {
  getCreatedYearFromTimesheet,
  getTimesheetStatus,
  isSTimesheet,
  shouldShowBillingQuestionBadge,
} from "@/pages/stundennachweise/timesheetFilterLogic";

/** Welche Startseite gezeigt wird. Bei mehreren Gruppen gilt die erste passende. */
export type HomePersona = "management" | "billing" | "office" | "field" | "general";

export function resolveHomePersona(user: CurrentUser | null): HomePersona {
  if (!user) return "general";
  if (userIsManagement(user)) return "management";
  if (userIsAbrechnung(user)) return "billing";
  if (userIsBuero(user)) return "office";
  if (userIsMonteure(user)) return "field";
  return "general";
}

export const HOME_PERSONA_TITLE: Record<HomePersona, string> = {
  management: "Geschäftsführung",
  billing: "Abrechnung",
  office: "Büro",
  field: "Monteure",
  general: "Dashboard",
};

export const HOME_PERSONA_SUBTITLE: Record<HomePersona, string> = {
  management: "Fertigstellungen, Lieferscheine beim Ansprechpartner und offene Rückfragen.",
  billing: "Unterschriebene Nachweise, Lieferscheine, Rechnungsentwürfe und Kontakte ohne Nummer.",
  office: "Material, Planung, Angebote und die letzten Aufträge.",
  field: "Deine Einsätze, bereitgelegtes Material und Nachrichten.",
  general: "Die wichtigsten Punkte kompakt auf einen Blick.",
};

function byRecentTimesheet(a: TimesheetSummary, b: TimesheetSummary): number {
  const ta = Date.parse(String(a.period_end || a.created_at || "")) || 0;
  const tb = Date.parse(String(b.period_end || b.created_at || "")) || 0;
  if (tb !== ta) return tb - ta;
  return Number(b.id || 0) - Number(a.id || 0);
}

export interface BillingHomeTimesheets {
  signed: TimesheetSummary[];
  drafts: TimesheetSummary[];
  questions: TimesheetSummary[];
}

/** Rechnungsentwurf im Listenfilter „aktuelles Jahr“, ohne S-Ordner. */
export function isCurrentListYearDraft(
  ts: TimesheetSummary,
  year = new Date().getFullYear(),
): boolean {
  return (
    getTimesheetStatus(ts) === "invoice_draft" &&
    !isSTimesheet(ts) &&
    getCreatedYearFromTimesheet(ts) === year
  );
}

/** Aktuelle Stundennachweise, aufgeteilt wie die Abrechnungs-Badges. */
export function splitBillingHomeTimesheets(
  rows: TimesheetSummary[],
  year = new Date().getFullYear(),
): BillingHomeTimesheets {
  const current = rows.filter((ts) => !ts.has_newer_revision);
  return {
    signed: current.filter((ts) => getTimesheetStatus(ts) === "signed").sort(byRecentTimesheet),
    drafts: current.filter((ts) => isCurrentListYearDraft(ts, year)).sort(byRecentTimesheet),
    questions: current
      .filter((ts) => shouldShowBillingQuestionBadge(ts))
      .sort(byRecentTimesheet),
  };
}

/** Gelieferte Lieferscheine, die die Abrechnung noch nicht gesehen hat. */
export function filterNewBillingLieferscheine(rows: LieferscheinSummary[]): LieferscheinSummary[] {
  return rows
    .filter((row) => row.is_new_for_billing)
    .sort((a, b) => {
      const ta = Date.parse(String(a.customer_signed_at || a.updated_at || a.created_at || "")) || 0;
      const tb = Date.parse(String(b.customer_signed_at || b.updated_at || b.created_at || "")) || 0;
      if (tb !== ta) return tb - ta;
      return Number(b.id || 0) - Number(a.id || 0);
    });
}
